import React, { useState } from "react";
import { Form, Input, Button, message } from "antd";
import { useNavigate } from "react-router-dom";
import api from "../../api";
import { observer } from "mobx-react-lite";
import { MyInput } from "../../components/MyInput";
import { FormRequiredMark } from "../../components/FormRequiredMark";
import { MySelect } from "../../components/MySelect";
import { MyTimeLine } from "../../components/MyTimeLine";
import { MyColor } from "../../config/myStyle";
import PageBox from "../../layout/PageBox";
import { useTranslation } from "react-i18next";

const salutationOptions = [
  { label: "Mr", value: "Mr" },
  { label: "Ms", value: "Ms" },
];

const RegisterForm = observer(() => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const navigate = useNavigate();

  // 验证码相关
  const [codeSent, setCodeSent] = useState(false);
  const [passcode, setPasscode] = useState('');
  const [codeTime, setCodeTime] = useState(0);
  const [sendingCode, setSendingCode] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [submittingNext, setSubmittingNext] = useState(false);
  const [emailVerified, setEmailVerified] = useState(false);

  const resetEmailVerificationState = () => {
    // 邮箱一旦变更或命中已注册，都必须清空当前验证上下文重新验证。
    setEmailVerified(false);
    setCodeSent(false);
    setPasscode("");
    setCodeTime(0);
  };

  React.useEffect(() => {
    if (!codeSent || codeTime <= 0) return;
    const timer = setTimeout(() => setCodeTime(c => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [codeSent, codeTime]);

  const handleSendCode = async () => {
    const email = form.getFieldValue("email");
    if (!email) {
      message.error(t("register_form.please_enter_email"));
      return;
    }
    try {
      // 发送验证码期间锁住按钮，防止用户重复点击。
      setSendingCode(true);
      const res = await api.PostAuthSendVerificationCode({ username: email, type: "register" });
      if (res.kind === 'ok') {
        setCodeSent(true);
        setCodeTime(60);
        message.success(t("register_form.code_sent"));
      } else {
        message.error(res.message || t("register_form.send_code_failed"));
      }
    } finally {
      setSendingCode(false);
    }
  };

  const handleVerifyCode = async () => {
    try {
      setVerifying(true);
      const email = form.getFieldValue("email");
      const res = await api.PostAuthConfirmEmail({
        email,
        passcode,
        type: "register",
      });
      if (res.kind === 'ok') {
        setEmailVerified(true);
        message.success(t("register_form.email_verified"));
      } else {
        message.error(t("register_form.code_invalid"));
      }
    } finally {
      setVerifying(false);
    }
  };

  const checkAccountExists = async (
    username: string,
    errorMessageKey: "register_form.phone_already_registered" | "register_form.email_already_registered"
  ) => {
    const res = await api.PostAuthCheckAccountExists({ username });
    if (res.kind !== "ok") {
      message.error(res.message || t("register_form.check_account_failed"));
      return true;
    }
    if (res.data.exists) {
      message.error(t(errorMessageKey));
      return true;
    }
    return false;
  };

  const onFinish = async (values: any) => {
    if (!emailVerified) {
      message.warning(t("register_form.please_verify_email"));
      return;
    }
    try {
      // 下一步期间锁住表单，避免重复触发账号检查和跳转。
      setSubmittingNext(true);
      const phone = `+852${String(values.phone).trim()}`;
      const phoneExists = await checkAccountExists(phone, "register_form.phone_already_registered");
      if (phoneExists) {
        return;
      }
      const emailExists = await checkAccountExists(values.email, "register_form.email_already_registered");
      if (emailExists) {
        // 命中邮箱已注册时回退验证状态，避免用户无法编辑邮箱。
        resetEmailVerificationState();
        return;
      }
      navigate("/register/form-b", {
        state: {
          chineseName: values.chineseName,
          salutation: values.salutation,
          englishGivenName: values.englishGivenName,
          englishSurname: values.englishSurname,
          email: values.email,
          phone,
          passcode,
        }
      });
    } finally {
      setSubmittingNext(false);
    }
  };

  return (
    <PageBox spinning={sendingCode || verifying || submittingNext}>
      <div style={{ padding: 18 }}>
        <div className='text-[25px]' style={{ color: MyColor.blue }}>
          {t("register_form.register")}
        </div>
        <div className='text-[20px]' style={{ color: MyColor.blue, opacity: 0.8 }}>
          {t("register_form.welcome")}
        </div>
        <MyTimeLine className="py-[24px]" current={0} steps={2} />
        <div className="p-[18px] pt-[30px]" style={{ backgroundColor: 'white', borderRadius: 20 }}>
          <div className="text-[18px] text-[#B2A292]" >{t("register_form.profile")}</div>
          <div className="flex text-[13px] text-[rgba(0,0,0,0.5)] mb-[30px]">
            <div className="pr-[3px]">*</div>
            <div>{t("register_form.profile_hint")}</div>
          </div>
          <Form scrollToFirstError={true} requiredMark={FormRequiredMark} form={form} layout="vertical" onFinish={onFinish}>
            <Form.Item
              label={t("register_form.chinese_name")}
              name="chineseName"
              rules={[{ required: false, message: t("register_form.please_enter_chinese_name") }]}
            >
              <MyInput placeholder={t("register_form.chinese_name_placeholder")} />
            </Form.Item>
            <Form.Item
              label={t("register_form.english_surname")}
              name="englishSurname"
              rules={[{ required: true, message: t("register_form.please_enter_english_surname") }]}
            >
              <MyInput placeholder={t("register_form.english_surname_placeholder")} />
            </Form.Item>
            <Form.Item
              label={t("register_form.english_given_name")}
              name="englishGivenName"
              rules={[{ required: true, message: t("register_form.please_enter_english_given_name") }]}
            >
              <MyInput placeholder={t("register_form.english_given_name_placeholder")} />
            </Form.Item>
            <Form.Item
              label={t("register_form.salutation")}
              name="salutation"
              rules={[{ required: true, message: t("register_form.please_select_salutation") }]}
            >
              <MySelect placeholder={t("register_form.salutation")} options={salutationOptions} />
            </Form.Item>
            <Form.Item
              label={t("register_form.phone")}
              name="phone"
              rules={[
                { required: true, message: t("register_form.please_enter_phone") },
                { pattern: /^\d{6,20}$/, message: t("register_form.please_enter_valid_phone") },
              ]}
            >
              <MyInput placeholder={t("register_form.phone")} maxLength={20} />
            </Form.Item>
            <Form.Item
              label={t("register_form.email")}
              name="email"
              rules={[
                { required: true, message: t("register_form.please_enter_email") },
                { type: "email", message: t("register_form.invalid_email") },
              ]}
            >
              <MyInput
                placeholder={t("register_form.email")}
                disabled={false}
                onChange={() => {
                  if (emailVerified || codeSent || passcode) {
                    resetEmailVerificationState();
                  }
                }}
              />
            </Form.Item>
            {!emailVerified && (
              <>
                <Button
                  style={{
                    width: '100%', borderRadius: 40, height: 35, color: 'white', backgroundColor: 'rgba(0,0,0,0.4)', fontSize: 15,
                    marginBottom: 10,
                  }}
                  disabled={codeTime > 0 || sendingCode || verifying || submittingNext}
                  loading={sendingCode}
                  onClick={handleSendCode}
                >
                  {t("register_form.send_verification_code")}
                </Button>
                {codeTime > 0 && (
                  <div className="pt-[10px] text-[rgba(0,0,0,0.4)] ">
                    {t("register_form.send_again_after", { seconds: codeTime })}
                  </div>
                )}
              </>
            )}
            {codeSent && (
              <div className="flex py-[20px]" style={{ alignItems: 'center' }}>
                <Input.OTP
                  styles={{
                    input: {
                      height: 40,
                      width: 40,
                    }
                  }}
                  length={4}
                  type='number'
                  onInput={e => setPasscode(e.join(''))}
                />
                <Button
                  className={`flex-[1] h-[40px] ml-[10px] bg-[${MyColor.sub}] text-[16px]`}
                  loading={verifying}
                  onClick={handleVerifyCode}
                  type="primary"
                  disabled={!(passcode.length >= 4) || sendingCode || submittingNext}
                >
                  {t("register_form.confirm")}
                </Button>
              </div>
            )}
            {emailVerified && (
              <div
                className="mb-[16px] flex items-center justify-center rounded-[14px] px-[16px] py-[12px] text-[18px] font-[700]"
                style={{ background: MyColor.red, color: "white", boxShadow: "0 0 0 3px rgba(231,31,118,0.12)" }}
              >
                {t("register_form.verification_success")}
              </div>
            )}
            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                loading={submittingNext}
                disabled={!emailVerified || sendingCode || verifying}
                style={{
                  width: "100%",
                  borderRadius: 20,
                  opacity: emailVerified ? 1 : 0.5,
                  background: "#BE9E73",
                  height: 44,
                  fontSize: 18,
                  color: "white",
                  letterSpacing: 1,
                  fontWeight: 500,
                }}
              >
                {t("register_form.next_step")}
              </Button>
            </Form.Item>
          </Form>
        </div>
      </div>
    </PageBox>
  );
});

export default RegisterForm;
